import { useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import type { LocationData } from '@/shared/api/location'
import { getTimeSlots } from '@/shared/api/timeSlots'
import { useLocationRef } from '@/contexts/RefContext'
import { useAppDispatch, useAppSelector } from '@/store'
import {
	setOrderType,
	setTable,
	setCurrentRef,
	clearStoreSettingsError,
	selectSelectedOrderTypeGuid,
} from '@/store/ticketSlice'
import { PATHS } from '@/router/paths'
import { QUERY_STALE_TIMES } from '@/lib/queryConfig'
import { useFeatureFlag } from '@/providers/FeatureFlagProvider'
import { useIsDineInScan } from '@/hooks/useIsDineInScan'

const MAX_TABLE_NAME_LENGTH = 64
const TABLE_NAME_INJECTION_CHARS = /[<>"'`]/g

interface StartOrderProps {
	location: LocationData
	isStoreClosureActive: boolean
}

export function StartOrder({ location, isStoreClosureActive }: StartOrderProps) {
	const ref = useLocationRef()
	const [searchParams] = useSearchParams()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const hasNavigated = useRef(false)
	const menuFirstFlag = useFeatureFlag('ew-olo-skip-order-settings', false)
	const checkoutAddressCollectionEnabled = useFeatureFlag(
		'ew-olo-checkout-address-collection',
		false,
	)

	// Support Ember QR codes (?dineIn=true&tableNotes=...) and new format (?tableName=...)
	// tableName alone does not imply dine-in — confirmed with Ember (app/routes/application.js: ticket.dineIn = !!dineIn)
	const rawTable = searchParams.get('tableName') ?? searchParams.get('tableNotes')
	// Sanitize: strip injection chars, cap length before storing in Redux/localStorage (OWASP A03)
	const tableName = rawTable
		? rawTable.replace(TABLE_NAME_INJECTION_CHARS, '').trim().slice(0, MAX_TABLE_NAME_LENGTH) ||
			null
		: null
	const isDineInScan = useIsDineInScan()

	// Ember parity: ticket.dineIn is a singleton — once set it holds for the entire session
	// regardless of URL changes. In React, in-app navigation to menu (e.g. back from checkout)
	// uses PATHS.menu(ref) which strips DineIn params, making isDineInScan false on re-mount.
	// Reading existing Redux state bridges this gap: if DINE_IN is already active this session,
	// treat it as equivalent to a fresh scan so PATH 1 (ASAP/Takeout) cannot overwrite it.
	const existingOrderTypeGuid = useAppSelector(selectSelectedOrderTypeGuid)
	// Ember parity: ticket.dineIn is gated on the dineIn URL param alone — tableNotes is
	// separate. A ?dineIn=true scan with no tableName still sets ticket.dineIn = true for
	// the entire session. Guard on DINE_IN order type only, not on dineInTableName being set.
	const isDineInSession = isDineInScan || existingOrderTypeGuid === 'DINE_IN'
	const dineInOrderType = location.orderTypes.find((ot) => ot.ref === 'DINE_IN') ?? null
	const firstOrderType = location.orderTypes[0] ?? null
	const takeoutOrderType = location.orderTypes.find((ot) => ot.ref === 'TAKEOUT') ?? null
	const hasTakeout = takeoutOrderType !== null

	// Memoised to avoid a new array reference on every render, which would cause the useEffect
	// (which depends on this value) to re-fire unnecessarily on every render cycle.
	const nonDineInOrderTypes = useMemo(
		() => location.orderTypes.filter((ot) => ot.ref !== 'DINE_IN'),
		[location.orderTypes],
	)

	// AC15 (STO-3352): when ew-olo-checkout-address-collection is ON, delivery-only locations
	// (single non-dine-in type = DELIVERY) are eligible for the ASAP auto-skip —
	// address is collected at checkout instead.
	const deliveryOnlyOrderType =
		checkoutAddressCollectionEnabled &&
		!hasTakeout &&
		nonDineInOrderTypes.length === 1 &&
		nonDineInOrderTypes[0].ref === 'DELIVERY'
			? nonDineInOrderTypes[0]
			: null

	// The order type to use for PATH 1 ASAP slot check: TAKEOUT (existing) or DELIVERY-only (AC15)
	const asapSkipOrderType = takeoutOrderType ?? deliveryOnlyOrderType

	// Fetch time-slots for PATH 1/PATH 1b ASAP auto-skip — skip when flag is OFF, dine-in session
	// is active (PATH 3 handles it), or when no eligible skip order type exists.
	const shouldFetchTimeSlots = menuFirstFlag && !!asapSkipOrderType && !isDineInSession

	const { data: timeSlotsData, isError: isTimeSlotsError } = useQuery({
		queryKey: ['timeSlots', ref, asapSkipOrderType?.ref],
		queryFn: () => getTimeSlots(ref, asapSkipOrderType!.ref),
		enabled: shouldFetchTimeSlots,
		staleTime: QUERY_STALE_TIMES.SHORT,
	})

	useEffect(() => {
		if (hasNavigated.current) return

		// PATH 3 — QR-code dine-in scan or active DineIn session (Ember: ticket.dineIn singleton).
		// isDineInSession is true when URL has DineIn params (fresh scan) OR when Redux already
		// has DINE_IN set this session — prevents PATH 1 overwriting the order type on back navigation.
		// Prefer DINE_IN order type; fall back to first available if API didn't return it.
		if (isDineInSession) {
			const orderTypeForDineIn = dineInOrderType ?? firstOrderType
			if (orderTypeForDineIn) {
				dispatch(clearStoreSettingsError())
				dispatch(setCurrentRef(ref))
				dispatch(
					setOrderType({ guid: orderTypeForDineIn.ref, orderType: orderTypeForDineIn }),
				)
				if (tableName) dispatch(setTable(tableName))
				// Guard set before the isStoreClosureActive check intentionally — prevents
				// repeated Redux dispatches on every re-render while a closure is active.
				// Moving it inside the navigation block would allow re-dispatch on each render
				// until the closure clears. A closure lifting while a dine-in user waits on
				// this page is not a real use case — the guard placement is correct as-is.
				hasNavigated.current = true
				if (!isStoreClosureActive && !location.isClosed) {
					const dineInSearch = new URLSearchParams()
					dineInSearch.set('dineIn', 'true')
					if (tableName) dineInSearch.set('tableName', tableName)
					void navigate(`${PATHS.menu(ref)}?${dineInSearch.toString()}`, {
						replace: true,
					})
				}
				return
			}
			// No order type found — fall through to PATH 2
		}

		// Suppress PATH 1 and PATH 2 navigation when store is dynamically closed or permanently closed
		if (isStoreClosureActive || location.isClosed) return

		// FF OFF — ew-olo-skip-order-settings disabled: go directly to PATH 2, preserving Ember behaviour.
		// Pre-select default order type so the modal does not open empty (Ember: order-type-modal.js init()).
		if (!menuFirstFlag) {
			const defaultOrderType = takeoutOrderType ?? nonDineInOrderTypes[0] ?? null
			if (defaultOrderType) {
				dispatch(setCurrentRef(ref))
				dispatch(setOrderType({ guid: defaultOrderType.ref, orderType: defaultOrderType }))
			}
			hasNavigated.current = true
			void navigate(PATHS.orderSettings(ref), { replace: true })
			return
		}

		// Fail-safe to PATH 2 on API error — prevents blank screen when time-slots fetch fails
		if (shouldFetchTimeSlots && isTimeSlotsError) {
			const defaultOrderType = takeoutOrderType ?? nonDineInOrderTypes[0] ?? null
			if (defaultOrderType) {
				dispatch(setCurrentRef(ref))
				dispatch(setOrderType({ guid: defaultOrderType.ref, orderType: defaultOrderType }))
			}
			hasNavigated.current = true
			void navigate(PATHS.orderSettings(ref), { replace: true })
			return
		}

		// PATH 1 conditions met but time-slots not yet loaded — wait
		if (shouldFetchTimeSlots && !timeSlotsData) return

		// PATH 1 — ASAP auto-skip: TAKEOUT exists + ASAP available
		// PATH 1b (AC15 STO-3352) — same skip for DELIVERY-only when ew-olo-checkout-address-collection ON
		// Future slots existing does not block the skip — PO requirement 2026-06-23
		// KNOWN GAP: when 2 OTs exist (e.g. TAKEOUT + DELIVERY) and TAKEOUT has no ASAP but DELIVERY
		// does, DELIVERY's ASAP is not checked — user falls to PATH 2. Needs PO clarification: STO-3525.
		if (timeSlotsData && asapSkipOrderType) {
			const asapOk = timeSlotsData.asapTimeSlot?.isAvailable === true
			if (asapOk) {
				dispatch(clearStoreSettingsError())
				dispatch(setCurrentRef(ref))
				dispatch(
					setOrderType({ guid: asapSkipOrderType.ref, orderType: asapSkipOrderType }),
				)
				hasNavigated.current = true
				void navigate(PATHS.menu(ref), { replace: true })
				return
			}
		}

		// PATH 2 — fallback: show the order-settings modal.
		// Pre-select order type so the modal does not open empty — the modal fetches time-slots
		// for the selected order type on first render.
		// TAKEOUT takes priority regardless of array position; falls back to the first non-dine-in
		// type otherwise. Differs from Ember's order-type-modal.js init() which always defaults
		// to orderTypes.firstObject.id.
		const defaultOrderType = takeoutOrderType ?? nonDineInOrderTypes[0] ?? null
		if (defaultOrderType) {
			dispatch(setCurrentRef(ref))
			dispatch(setOrderType({ guid: defaultOrderType.ref, orderType: defaultOrderType }))
		}
		hasNavigated.current = true
		void navigate(PATHS.orderSettings(ref), { replace: true })
	}, [
		isDineInSession,
		tableName,
		dineInOrderType,
		firstOrderType,
		menuFirstFlag,
		isStoreClosureActive,
		location.isClosed,
		shouldFetchTimeSlots,
		timeSlotsData,
		isTimeSlotsError,
		asapSkipOrderType,
		nonDineInOrderTypes,
		takeoutOrderType,
		ref,
		dispatch,
		navigate,
	])

	return null
}
